import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import BusyBeeLogo from '../components/BusyBeeLogo';
import { ArrowRight, Target, Heart, Zap, Users, Globe } from 'lucide-react';

const VALUE_KEYS = [
  { key: 'all_in_one', icon: Target },
  { key: 'people_first', icon: Heart },
  { key: 'ai_powered', icon: Zap },
  { key: 'team_ready', icon: Users },
  { key: 'global', icon: Globe },
];

// A "Leadership Team" grid used to live here: Alex Rivera (CEO & Co-Founder),
// Jordan Chen (CTO & Co-Founder), Sam Williams (Head of Product) and Taylor Kim
// (Head of Design), each with an Unsplash stock portrait. None of these people
// exist. Presenting stock photographs of real individuals as a company's
// officers is worse than an exaggerated claim, so the block and its strings are
// removed outright rather than reworded.

const AboutPage = () => {
  const { t } = useTranslation('landing');

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <SEO
        title={t('about.seo.title')}
        description={t('about.seo.description')}
        canonical="/about"
        breadcrumbs={[
          { name: t('pricing.breadcrumb_home'), url: '/' },
          { name: t('about.breadcrumb_about') }
        ]}
      />
      <Navbar />

      <main className="pt-20 pb-24">
        {/* Hero */}
        <section className="max-w-4xl mx-auto px-4 py-16 text-center">
          <BusyBeeLogo className="h-16 justify-center mb-8" />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('about.hero.headline_a')}<br />{t('about.hero.headline_b')}
          </h1>
          <p className="text-xl text-gray-400">
            {t('about.hero.subtitle')}
          </p>
        </section>

        {/* Story */}
        <section className="max-w-4xl mx-auto px-4 py-16">
          <h2 className="text-3xl font-bold text-white mb-6">{t('about.story.headline')}</h2>
          <div className="prose prose-lg prose-invert">
            <p className="text-gray-400 leading-relaxed mb-4">{t('about.story.p1')}</p>
            {/* p3 claimed "over 50,000 professionals reclaim 8 hours a week".
                Removed - no such figure was ever measured. */}
            <p className="text-gray-400 leading-relaxed">{t('about.story.p2')}</p>
          </div>
        </section>

        {/* Values */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">{t('about.values.headline')}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {VALUE_KEYS.map((value) => {
              const Icon = value.icon;
              return (
                <div key={value.key} className="p-6 bg-white/5 rounded-2xl border border-gray-800">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-amber-400" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">{t(`about.values.items.${value.key}.title`)}</h3>
                  <p className="text-gray-400 text-sm">{t(`about.values.items.${value.key}.desc`)}</p>
                </div>
              );
            })}
          </div>
        </section>


        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">{t('about.cta.headline')}</h2>
          <p className="text-gray-400 mb-8">{t('about.cta.subtitle')}</p>
          <Link to="/careers" className="bg-amber-500 text-black px-8 py-4 rounded-xl font-semibold hover:bg-amber-400 transition-colors inline-flex items-center gap-2">
            {t('about.cta.button')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
